import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Play } from 'lucide-react';
import { VideoSelectModal } from '@/components/playlist/VideoSelectModal';
import { PlaylistVideoAccordion } from '@/components/playlist/PlaylistVideoAccordion';
import { PlaylistVideo } from '@/store/playlist/types';
import { ModuleVideoSection } from './ModuleVideoSection';
import { BonusVideoSection } from './BonusVideoSection';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

interface PlaylistVideosTabProps {
  videos: PlaylistVideo[];
  onVideosChange: (videos: PlaylistVideo[]) => void;
  template?: string;
  darkMembersSettings?: any;
  onDarkMembersSettingsChange?: (settings: any) => void;
}

// Mock videos disponíveis para seleção
const mockAvailableVideos = [
  {
    id: 'video-1',
    name: 'Introdução ao Marketing Digital',
    thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400',
    duration: 320,
    views: 1250,
  },
  {
    id: 'video-2',
    name: 'SEO Básico para Iniciantes',
    thumbnail: 'https://images.unsplash.com/photo-1432888622747-4eb9a8f2c293?w=400',
    duration: 480,
    views: 890,
  },
  {
    id: 'video-3',
    name: 'Google Ads - Primeiros Passos',
    thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400',
    duration: 420,
    views: 1100,
  },
];

export const PlaylistVideosTab: React.FC<PlaylistVideosTabProps> = ({
  videos,
  onVideosChange,
  template,
  darkMembersSettings,
  onDarkMembersSettingsChange,
}) => {
  useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newVideos = [...videos];
    [newVideos[index - 1], newVideos[index]] = [newVideos[index], newVideos[index - 1]];
    onVideosChange(newVideos.map((v, i) => ({ ...v, order: i })));
  };

  const handleMoveDown = (index: number) => {
    if (index === videos.length - 1) return;
    const newVideos = [...videos];
    [newVideos[index], newVideos[index + 1]] = [newVideos[index + 1], newVideos[index]];
    onVideosChange(newVideos.map((v, i) => ({ ...v, order: i })));
  };

  const handleAddVideos = (selectedVideoIds: string[]) => {
    const newVideos = selectedVideoIds
      .filter((id) => !videos.some((v) => v.videoId === id))
      .map((id, index) => ({
        id: `playlist-video-${Date.now()}-${index}`,
        videoId: id,
        order: videos.length + index,
      }));

    onVideosChange([...videos, ...newVideos]);
    setIsModalOpen(false);
  };

  const handleRemoveVideo = (videoId: string) => {
    const updatedVideos = videos
      .filter((v) => v.id !== videoId)
      .map((video, index) => ({
        ...video,
        order: index,
      }));

    onVideosChange(updatedVideos);
  };

  const handleUpdateVideo = (videoId: string, updates: Partial<PlaylistVideo>) => {
    const updatedVideos = videos.map((v) =>
      v.id === videoId ? { ...v, ...updates } : v
    );
    onVideosChange(updatedVideos);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = videos.findIndex((v) => v.id === active.id);
      const newIndex = videos.findIndex((v) => v.id === over.id);
      
      const newVideos = arrayMove(videos, oldIndex, newIndex).map((v, i) => ({
        ...v,
        order: i,
      }));
      
      onVideosChange(newVideos);
    }
  };

  const totalDuration = videos.reduce((acc, video) => {
    const videoData = mockAvailableVideos.find((v) => v.id === video.videoId);
    return acc + (videoData?.duration || 0);
  }, 0);

  const formatTotalDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes} min`;
  };

  // Verifica se é Dark Members com módulos/bônus
  const isDarkMembers = template === 'dark-members';
  const hasModulesOrBonuses = darkMembersSettings?.modules?.length > 0 || darkMembersSettings?.bonuses?.length > 0;

  const handleUpdateModule = (moduleId: string, updates: any) => {
    if (!onDarkMembersSettingsChange || !darkMembersSettings) return;

    const updatedModules = darkMembersSettings.modules.map((m: any) =>
      m.id === moduleId ? { ...m, ...updates } : m
    );

    onDarkMembersSettingsChange({
      ...darkMembersSettings,
      modules: updatedModules,
    });
  };

  const handleUpdateBonus = (bonusId: string, updates: any) => {
    if (!onDarkMembersSettingsChange || !darkMembersSettings) return;

    const updatedBonuses = darkMembersSettings.bonuses.map((b: any) =>
      b.id === bonusId ? { ...b, ...updates } : b
    );

    onDarkMembersSettingsChange({
      ...darkMembersSettings,
      bonuses: updatedBonuses,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header com contador e botão */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Play className="w-4 h-4" />
          <span>
            {videos.length} {videos.length === 1 ? translateInterfaceText("vídeo") : translateInterfaceText("vídeos")}
            {videos.length > 0 && translateInterfaceText(" • {value0} de conteúdo", { value0: (formatTotalDuration(totalDuration)) })}
          </span>
        </div>

        <Button
          onClick={() => setIsModalOpen(true)}
          variant="outline"
          size="sm"
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          {translateInterfaceText("Adicionar Vídeos")}</Button>
      </div>

      {/* Conteúdo Dinâmico: Módulos/Bônus ou Lista Plana */}
      {isDarkMembers && hasModulesOrBonuses ? (
        <div className="space-y-6">
          {/* Seção de Módulos */}
          {darkMembersSettings?.modules && darkMembersSettings.modules.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">
                {darkMembersSettings.modulesSectionTitle || translateInterfaceText("Módulos")}
              </h2>
              {darkMembersSettings.modules.map((module: any, index: number) => (
                <ModuleVideoSection
                  key={module.id}
                  module={module}
                  moduleIndex={index}
                  onUpdateModule={handleUpdateModule}
                  availableVideos={mockAvailableVideos}
                />
              ))}
            </div>
          )}

          {/* Separador */}
          {darkMembersSettings?.modules?.length > 0 && darkMembersSettings?.bonuses?.length > 0 && (
            <div className="border-t border-gray-200" />
          )}

          {/* Seção de Bônus */}
          {darkMembersSettings?.bonuses && darkMembersSettings.bonuses.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-foreground">
                {darkMembersSettings.bonusesSectionTitle || translateInterfaceText("Bônus")}
              </h2>
              {darkMembersSettings.bonuses.map((bonus: any, index: number) => (
                <BonusVideoSection
                  key={bonus.id}
                  bonus={bonus}
                  bonusIndex={index}
                  onUpdateBonus={handleUpdateBonus}
                  availableVideos={mockAvailableVideos}
                />
              ))}
            </div>
          )}
        </div>
      ) : videos.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={videos.map((v) => v.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-3 max-w-[70%]">
                {videos.map((video, index) => {
                  const videoData = mockAvailableVideos.find(v => v.id === video.videoId);
                  if (!videoData) return null;

                  return (
                    <PlaylistVideoAccordion
                      key={video.id}
                      video={video}
                      videoData={videoData}
                      index={index}
                      onRemove={() => handleRemoveVideo(video.id)}
                      onUpdateVideo={(updates) => handleUpdateVideo(video.id, updates)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed rounded-xl bg-muted/30">
          <Play className="w-12 h-12 text-muted-foreground/50 mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">
            {translateInterfaceText("Nenhum vídeo adicionado")}</p>
          <p className="text-sm text-muted-foreground mb-4">
            {translateInterfaceText("Adicione vídeos para começar a criar sua playlist")}</p>
          <Button onClick={() => setIsModalOpen(true)} variant="outline" className="gap-2">
            <Plus className="w-4 h-4" />
            {translateInterfaceText("Adicionar Primeiro Vídeo")}</Button>
        </div>
      )}

      {/* Modal de seleção de vídeos */}
      <VideoSelectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleAddVideos}
        availableVideos={mockAvailableVideos}
        selectedVideoIds={videos.map((v) => v.videoId)}
      />
    </div>
  );
};